<template>
    <div style="max-width: 80%; margin: auto;">
        <ag-grid-vue v-if="row_data !== undefined" class="ag-theme-alpine" :columnDefs="column_defs"
        :defaultColDef="default_cols" :rowData="row_data" id="patient_table"
        :style="`height: ${grid_height}px; width: auto; margin-top: 20px;`" rowHeight="50"
        :alwaysShowHorizontalScroll="false" :pagination="true" :paginationPageSize="16" :gridOptions="gridOptions"
        @grid-ready="onGridReady" />
    </div>
    
</template>

<script setup lang="js">
import { AgGridVue } from 'ag-grid-vue3';
import { onMounted, onUnmounted, ref, computed, watch, } from 'vue';
import { useStore } from 'vuex';
import id_requests from '@/methods/id_requests';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import agg from '@/methods/ag_grid_toolbox';
import TooltipHeader from './TooltipHeader.vue';


const store = useStore();
const column_defs = computed(() => store.state.ag_grid_coldefs[props?.table_id]);
const row_data = computed(() => store.state.ag_grid_rowdata[props?.table_id]);
const row_keys = computed(() => store.state.ag_grid_rowkeys[props?.table_id]);
const current_mtb = computed(() => store.state.current_mtb);

const default_cols = ref()
/* Grid height is set to 16*50 + 100 for 16 rows*/
const grid_height = computed(() => {
    var temp_height = Math.min(900, (row_keys.value.length + 2) * 50);
    if (props?.maxHeight) temp_height = Math.min(props?.maxHeight, temp_height);
    return temp_height;
})

const gridOptions = {};

/**
 * @prop maxHeight - Can be passed to limit this table's maximum height (in px).
 * @prop table_id - ID for the table. Important for HTML query selections, so it should be unique.
 */
const props = defineProps({
    maxHeight: Number,
    table_id: String,
})

/**
 * When this table is loaded, the following steps are performed:
 * 1. Patients for the currently selected MTB (newest by default) are fetched from server and saved in idList (store)
 * 2. fetchPatients() is called, which normalizes the patient IDs and stores them for presentation
 *  Those are also the patient data used for the table
 * 3. Default column definitions from ag_grid_toolbox are used, patient data is converted to AG Grid row data
 * 4. Dedicated column definitions are created for the patient table
 * 5. Window listeners are added, columns are pre-sized, table goes to page 0
 */
onMounted(async () => {
    await id_requests.getMTBData(current_mtb.value);
    store.dispatch("setIDList", store.state.mtb_data[current_mtb.value]);
    agg.fetchPatients();

    default_cols.value = agg.createDefaultColDefs();
    patientsToRowData();
    createColDefs();
    gridOptions.api?.sizeColumnsToFit();
    gridOptions.api?.paginationGoToPage(0);
    window.addEventListener('sort-grid', (e) => onSort(e))
    window.addEventListener('resize', resizeGrid);
});

/**
 * Event listeners for sorting and resizing are removed on unmounting the component.
 */
onUnmounted(() => {
  window.removeEventListener('sort-grid', (e) => onSort(e))
  window.removeEventListener('resize', resizeGrid);
});

/**
 * Called when the sort-grid window event is caught. Applies a specific sort to the grid and refreshes it.
 * Will sort based on a targeted column based on a provided sorting method (asc, desc, none)
 * @param {CustomEvent} e - Event that contains sorting information (column, desired order)
 */
const onSort = (e) => {
    gridOptions.columnApi?.applyColumnState({
        state: [{ colId: e.detail.column.toLowerCase(), sort: e.detail.order }],
        defaultState: { sort: null },
    });
    gridOptions.api?.refreshCells();
}

/**
 * Once the grid is ready, it is refreshed and resized.
 */
const onGridReady = () => {
    gridOptions.api?.refreshCells();
    resizeGrid();
}

/**
 * Resizes/re-renders specific elements of the grid based on the screen width.
 * Sizes should be set in accordance with any media queries.
 */
const resizeGrid = () => {
    gridOptions.api?.sizeColumnsToFit();
    let current_coldefs = gridOptions.api?.getColumnDefs();
    if (!current_coldefs) return;

    // Sizes below 1300
    if (window.innerWidth < 1300) {
        current_coldefs.forEach((def, _) => {
            def.headerComponent = null;
            def.headerName = shortened_headers[def.colId];
            // Sizes below 1000
            if (window.innerWidth < 1000) {
                if (def.colId == 'patient_interpretation' || def.colId == 'patient_details') {
                    def.width = '60';
                    def.maxWidth = '60';
                    def.minWidth = '60';
                    def.cellRenderer = (params) => def.colId == 'patient_details' ? 
                    addInfoButton(params?.data.patient_id, "small") : addInterpretationButton(params?.data.patient_id, "small")
                }

            }
            // Sizes between 1000 and 1300
            else {
                if (def.colId == 'patient_interpretation' || def.colId == 'patient_details') {
                    def.width = '80';
                    def.maxWidth = '80';
                    def.minWidth = '80';
                    def.cellRenderer = (params) => def.colId == 'patient_details' ? 
                    addInfoButton(params?.data.patient_id, "small") : addInterpretationButton(params?.data.patient_id, "small")
                }
            }
        });
    }
    // Normal size (> 1300)
    if (window.innerWidth > 1300) {
        current_coldefs.forEach((def, index) => {
            def.headerComponent = TooltipHeader;
            def.headerName = long_headers[def.colId];
            if (def.colId == 'patient_interpretation' || def.colId == 'patient_details') {
                    def.width = '150';
                    def.maxWidth = '150';
                    def.minWidth = '150';
                    def.cellRenderer = (params) => def.colId == 'patient_details' ? 
                    addInfoButton(params?.data.patient_id, "full") : addInterpretationButton(params?.data.patient_id, "full")
                }
        })
    }
    gridOptions.api?.setColumnDefs(current_coldefs);
    gridOptions.api?.redrawRows();
}

/**
 * Creates table column definitions. These are specific for this table.
 * Saves the definitions in the column_defs ref (see below) and applies them to the grid.
 * If keys and key_values refs are defined below, they are used to automatically create some column definitions.
 */
const createColDefs = () => {
    let new_column_defs = [];

    // Patient ID
    new_column_defs.push({
        headerName: "Patient ID", field: 'patient_id', headerClass: 'presentation-header-left',
        pinned: 'left', headerComponent: TooltipHeader, headerComponentParams: { title: 'Patient ID', tooltip_id: 'patient_id' },
        maxWidth: 200,
    })

    // Interpretation Column
    new_column_defs.push({
        headerName: 'Interpretation', colId: 'patient_interpretation', headerClass: 'presentation-header',
        headerComponent: TooltipHeader, headerComponentParams: { title: 'Interpretation', colid: 'patient_interpretation', tooltip_id: 'patient_interpretation' },
        cellRenderer: (params) => addInterpretationButton(params?.data.patient_id, "full"),
        cellStyle: { justifyItems: "flex-center" }, minWidth: 50,
    });

    // Variants
    new_column_defs.push({
        headerName: 'Variants', colId: 'patient_variants', headerClass: 'presentation-header-left',
        headerComponent: TooltipHeader, headerComponentParams: { title: 'Variants', tooltip_id: 'patient_variants' },
        field: 'patient_variants'
    });

    // Details Column
    new_column_defs.push({
        headerName: 'Details', colId: 'patient_details', headerClass: 'presentation-header',
        headerComponent: TooltipHeader, headerComponentParams: { title: 'Details', colid: 'patient_details', tooltip_id: 'patient_details' },
        cellRenderer: (params) => addInfoButton(params?.data.patient_id, "full"),
        cellStyle: { justifyItems: "flex-center" }, pinned: 'right', flex: 0, maxWidth: 150, minWidth: 150,
    });



    store.dispatch('setAGGColdefs', { defs: new_column_defs, id: props?.table_id });
    gridOptions.api?.setColumnDefs(new_column_defs);
};

/**
 * Turns clinical significance data into rows for the AG Grid.
 * Uses two refs (see below): row_data and keys.
 */
const patientsToRowData = () => {

    // Check if data is present
    var list_to_use = store.getters.idList;
    if (!list_to_use || list_to_use == {}) return;

    var new_row_data = [];

    for (const [_, patient] of Object.entries(list_to_use)) {
        let new_row = {
            patient_id: patient.id,
            patient_variants: patient.biomarkers ? Object.keys(patient.biomarkers).length : 0,
        };
        new_row_data.push(new_row);
    }

    store.dispatch('setAGGRowdata', { data: new_row_data, id: props?.table_id });
    store.dispatch('setAGGRowkeys', { keys: Object.keys(list_to_use), id: props?.table_id });

};

/**
 * Adds an "info" button that will navigate to the patient details page for the particular patient.
 * @param {String} patient_id - ID of the patient to show
 * @param {String} size - Either "full" or "small", dictates the content/size of the button
 */
const addInfoButton = (patient_id, size = "full") => {
    const info_button = document.createElement("div");

    info_button.innerText = "Details";

    if (patient_id && patient_id != '') {
        info_button.classList.add("details-button");
    } else {
        info_button.classList.add("details-button-disabled");
    }

    info_button.classList.add("text-button");
    if (size != "full") {
        info_button.innerText = 'D.'
        info_button.classList.add("details-button-small")
    }
    info_button.addEventListener("click", e => {
        if (patient_id && patient_id != '') {
            router.push(`/show_id/${patient_id}`);
        }
    });
    return info_button;
}

/**
 * Adds a "run interpretation" button that will at some point run an interpretation pipeline for the patient.
 * @param {String} patient_id - ID of the patient to run the pipeline for
 * @param {String} size - Either "full" or "small", dictates the content/size of the button
 */
const addInterpretationButton = (patient_id, size = "full") => {
    const interpret_button = document.createElement("div");

    interpret_button.innerText = "Run Int.";

    if (patient_id && patient_id != '') {
        interpret_button.classList.add("details-button");
    } else {
        interpret_button.classList.add("details-button-disabled");
    }

    interpret_button.classList.add("text-button");
    if (size != "full") {
        interpret_button.innerText = 'Run'
        interpret_button.classList.add("details-button-small")
    }
    interpret_button.addEventListener("click", e => {
        if (patient_id && patient_id != '') {
            alert('This is not a working feature yet!');
        }
    });
    return interpret_button;
}


/**
 * Redraws the grid once the loading process stops.
 * Somehow refreshCells() is not enough here, so all rows have to be redrawn.
 */
watch(() => store.getters.getProcessState('load-results'), () => {
    gridOptions.api?.redrawRows();
    gridOptions.api?.sizeColumnsToFit();
});

/**
 * Updates the patient table if a new MTB is selected.
 */
watch(() => current_mtb.value, async () => {
    await id_requests.getMTBData(current_mtb.value);
    store.dispatch("setIDList", store.state.mtb_data[current_mtb.value]);
    agg.fetchPatients();
});

const shortened_headers = {
    'patient_id': 'ID',
    'patient_variants': 'Var.',
    'patient_interpretation': 'Int.',
    'patient_details': 'Dt.',
};
const long_headers = {
    'patient_id': 'Patient ID',
    'patient_variants': 'Variants',
    'patient_interpretation': 'Interpretation',
    'patient_details': 'Details',
}
</script>

<style scoped>
</style>